import { z } from 'zod';

import type { ActivityKind } from './activity';
import { ActorLoginSchema } from './github-login';

// ---------------------------------------------------------------------------
// The per-kind `detail` payload carried by an activity event.
//
// Split from `activity.ts` so the DTO/enum layer stays separate from payload
// validation, and imports one way only (this module depends on the kinds, never
// the reverse).
//
// User-authored prose is stored only as a bounded plain-text excerpt. React
// renders it as text (never HTML), and the MCP activity formatters deliberately
// omit `detail`, so a comment cannot become agent instructions through this
// contract. Control characters are removed at ingestion while useful line
// breaks are preserved for the card preview.
// ---------------------------------------------------------------------------

const prNumber = z.number().int().positive();
const ActivityExcerptSchema = z.string().min(1).max(500);

function isBidirectionalControl(codePoint: number): boolean {
  return (
    codePoint === 1564 ||
    (codePoint >= 8206 && codePoint <= 8207) ||
    (codePoint >= 8234 && codePoint <= 8238) ||
    (codePoint >= 8294 && codePoint <= 8297)
  );
}

function isActivityTextCharacter(character: string): boolean {
  const codePoint = character.codePointAt(0) ?? 0;
  if (isBidirectionalControl(codePoint)) return false;
  return (
    codePoint === 9 ||
    codePoint === 10 ||
    codePoint === 13 ||
    (codePoint >= 32 && codePoint <= 126) ||
    codePoint >= 160
  );
}

function truncateWithoutSplittingCharacter(
  value: string,
  maxLength: number,
): string {
  let result = '';
  for (const character of value) {
    if (result.length + character.length > maxLength) break;
    result += character;
  }
  return result;
}

/** A safe, bounded preview of user-authored GitHub prose. */
export function toActivityExcerpt(
  body: string | null | undefined,
): string | undefined {
  if (body === null || body === undefined) return undefined;
  // Keep tabs/newlines, which carry useful comment formatting, but remove
  // terminal/control characters that have no place in a feed card.
  const plainText = [...body].filter(isActivityTextCharacter).join('').trim();
  if (plainText === '') return undefined;
  return truncateWithoutSplittingCharacter(plainText, 500);
}

/** Bounded, prose-free facts shared by the broader GitHub action kinds. */
const GithubActionDetailSchema = z.object({
  action: z.string().min(1).max(64),
  number: z.number().int().positive().optional(),
  target: z.string().min(1).max(256).optional(),
  ref: z.string().min(1).max(256).optional(),
  commitCount: z.number().int().nonnegative().optional(),
  commits: z
    .array(
      z.object({
        sha: z.string().min(7).max(64),
        message: z.string().min(1).max(256),
      }),
    )
    .max(20)
    .optional(),
  bodyExcerpt: ActivityExcerptSchema.optional(),
});

const ReviewDetailSchema = z.object({
  prNumber,
  reviewerLogin: ActorLoginSchema,
  inlineCommentCount: z.number().int().nonnegative(),
  bodyExcerpt: ActivityExcerptSchema.optional(),
});

const PrCommentDetailSchema = z.object({
  prNumber,
  mentionCount: z.number().int().nonnegative(),
  bodyExcerpt: ActivityExcerptSchema.optional(),
});

const ReviewRequestedDetailSchema = z.object({
  prNumber,
  requestedLogin: ActorLoginSchema,
});

const ReviewRequestedTeamDetailSchema = z.object({
  prNumber,
  teamSlug: z.string().min(1).max(100),
});

const AssignedDetailSchema = z.object({
  prNumber,
  assigneeLogin: ActorLoginSchema,
});

const PrLifecycleDetailSchema = z.object({
  prNumber,
  /**
   * Nullable, matching the event-level `actorLogin`. The webhook always knows
   * who closed a PR; a poll does not — GraphQL exposes no "closed by" without a
   * timeline query — and attributing someone else's close to the author would
   * be worse than admitting we don't know. Widening is backward-compatible:
   * every row written so far carries a login.
   */
  actorLogin: ActorLoginSchema.nullable(),
});

const CheckRunFailedDetailSchema = z.object({
  checkName: z.string().min(1).max(256),
  conclusion: z.enum(['failure', 'timed_out', 'cancelled']),
  branch: z.string().min(1).max(256),
  /** Null for a branch-only failure — those never produce a receipt. */
  prNumber: prNumber.nullable(),
  checkUrl: z.url({ protocol: /^https?$/ }).optional(),
});

const ScanDeltaDetailSchema = z.object({
  metric: z.string().min(1).max(64),
  previousValue: z.number(),
  currentValue: z.number(),
  headSha: z.string().min(7).max(40),
});

const ScanFindingDetailSchema = z.object({
  ruleId: z.string().min(1).max(128),
  path: z.string().min(1).max(1024),
  severity: z.enum(['low', 'medium', 'high', 'critical']),
  headSha: z.string().min(7).max(40),
});

// A RESOLVED finding carries the same shape as a new one — it names the rule
// and file that stopped being flagged. `headSha` is the scan that no longer
// reports it, which is what makes the source id stable and the event a no-op
// on re-run.
const ScanResolvedDetailSchema = ScanFindingDetailSchema;

const NextUpChangedDetailSchema = z.object({
  previousItemId: z.string().max(256).nullable(),
  currentItemId: z.string().min(1).max(256),
});

/**
 * Declared as a full record so a new kind that forgets a schema is a compile
 * error, then read through a Map: indexing a record with a variable key trips
 * `security/detect-object-injection`, and that rule is right in general even
 * though the key here is a validated enum member.
 */
const DETAIL_SCHEMA_BY_KIND = {
  commits_pushed: GithubActionDetailSchema,
  branch_created: GithubActionDetailSchema,
  branch_deleted: GithubActionDetailSchema,
  tag_created: GithubActionDetailSchema,
  tag_deleted: GithubActionDetailSchema,
  commit_commented: GithubActionDetailSchema,
  pr_opened: GithubActionDetailSchema,
  pr_reopened: GithubActionDetailSchema,
  pr_ready_for_review: GithubActionDetailSchema,
  pr_converted_to_draft: GithubActionDetailSchema,
  pr_metadata_changed: GithubActionDetailSchema,
  issue_opened: GithubActionDetailSchema,
  issue_reopened: GithubActionDetailSchema,
  issue_closed: GithubActionDetailSchema,
  issue_commented: GithubActionDetailSchema,
  issue_metadata_changed: GithubActionDetailSchema,
  review_approved: ReviewDetailSchema,
  review_changes_requested: ReviewDetailSchema,
  review_commented: ReviewDetailSchema,
  review_comment_replied: ReviewDetailSchema,
  review_dismissed: ReviewDetailSchema,
  pr_comment: PrCommentDetailSchema,
  review_requested: ReviewRequestedDetailSchema,
  review_requested_team: ReviewRequestedTeamDetailSchema,
  assigned: AssignedDetailSchema,
  pr_merged: PrLifecycleDetailSchema,
  pr_closed_unmerged: PrLifecycleDetailSchema,
  check_run_failed: CheckRunFailedDetailSchema,
  scan_delta: ScanDeltaDetailSchema,
  scan_finding: ScanFindingDetailSchema,
  scan_resolved: ScanResolvedDetailSchema,
  next_up_changed: NextUpChangedDetailSchema,
} as const satisfies Record<ActivityKind, z.ZodTypeAny>;

const DETAIL_SCHEMA_LOOKUP = new Map<ActivityKind, z.ZodTypeAny>(
  Object.entries(DETAIL_SCHEMA_BY_KIND) as readonly [
    ActivityKind,
    z.ZodTypeAny,
  ][],
);

export type ActivityDetail<K extends ActivityKind = ActivityKind> = z.infer<
  (typeof DETAIL_SCHEMA_BY_KIND)[K]
>;

/** The `detail` schema a kind must satisfy. Total by construction. */
export function detailSchemaForKind(kind: ActivityKind): z.ZodTypeAny {
  const schema = DETAIL_SCHEMA_LOOKUP.get(kind);
  if (schema === undefined) throw new Error(`UNKNOWN_ACTIVITY_KIND:${kind}`);
  return schema;
}

/**
 * Serialise a `detail` payload, refusing anything the kind doesn't allow.
 *
 * Throws on mismatch: a malformed detail is a writer bug, and letting it land
 * would put a row in the table that no reader can interpret.
 */
export function serialiseActivityDetail(
  kind: ActivityKind,
  detail: unknown,
): string {
  return JSON.stringify(detailSchemaForKind(kind).parse(detail));
}

/**
 * Parse a stored `detail` string, never throwing.
 *
 * Returns `null` when the row predates a schema change or was written by a
 * newer version — the UI degrades that row to its title rather than failing the
 * whole page. A feed that 500s because one historical row drifted is worse than
 * a feed with one thin row.
 */
export function safeParseActivityDetail(
  kind: ActivityKind,
  raw: string,
): ActivityDetail | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    const result = detailSchemaForKind(kind).safeParse(parsed);
    return result.success ? (result.data as ActivityDetail) : null;
  } catch {
    return null;
  }
}
